import { graph, getAccessToken } from './graphClient';

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID;

export const LIST_NAMES = {
  team: import.meta.env.VITE_LIST_TEAM || 'TeamMembers',
  plants: import.meta.env.VITE_LIST_PLANTS || 'Plants',
  suppliers: import.meta.env.VITE_LIST_SUPPLIERS || 'Suppliers',
  supplierDocuments:
    import.meta.env.VITE_LIST_SUPPLIER_DOCUMENTS || 'SupplierDocuments',
  parts: import.meta.env.VITE_LIST_PARTS || 'Parts',
  reports: import.meta.env.VITE_LIST_REPORTS || 'Reports',
  billing: import.meta.env.VITE_LIST_BILLING || 'BillingEntries',
  invoices: import.meta.env.VITE_LIST_INVOICES || 'Invoices',
  updates: import.meta.env.VITE_LIST_UPDATES || 'CompanyUpdates',
  agreements: import.meta.env.VITE_LIST_AGREEMENTS || 'Agreements',
  salesResources: import.meta.env.VITE_LIST_SALES_RESOURCES || 'SalesResources',
  teamMemberResources:
    import.meta.env.VITE_LIST_TEAM_MEMBER_RESOURCES || 'TeamMemberResources',
  requests: import.meta.env.VITE_LIST_REQUESTS || 'Requests',
  salesTrackerEvents:
    import.meta.env.VITE_LIST_SALES_TRACKER_EVENTS || 'SalesTrackerEvents',
  timesheets: import.meta.env.VITE_LIST_TIMESHEETS || 'Timesheets',
  timesheetReceipts:
    import.meta.env.VITE_LIST_TIMESHEET_RECEIPTS || 'TimesheetReceipts',
};

function assertSite() {
  if (!SITE_ID) {
    throw new Error(
      'VITE_SHAREPOINT_SITE_ID is not configured. Set it in .env.local.',
    );
  }
}

const listBase = (listKey) => {
  assertSite();
  const name = LIST_NAMES[listKey] || listKey;
  return `/sites/${SITE_ID}/lists/${name}`;
};

/**
 * Fetch all items from a list. Transparently follows @odata.nextLink so callers
 * get a single flat array. Expands `fields` since the list schema lives there.
 *
 * @param {string} listKey - key from LIST_NAMES (e.g. 'suppliers')
 * @param {object} [opts]
 * @param {string} [opts.select] - comma-separated field names to include
 * @param {string} [opts.filter] - OData $filter expression (applied to fields/...)
 * @param {string} [opts.orderby] - OData $orderby expression
 * @param {number} [opts.top] - page size hint
 */
export async function listItems(listKey, opts = {}) {
  const { select, filter, orderby, top = 200 } = opts;
  let req = graph
    .api(`${listBase(listKey)}/items`)
    // SharePoint refuses filter/orderby on columns that aren't explicitly
    // indexed. Our lists are small (well under the 5000-item threshold) so
    // we opt into "non-indexed query" behaviour — this is the Graph-documented
    // header for doing so. If a list ever grows large, we should index the
    // relevant columns in SharePoint admin instead.
    .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
    .expand(select ? `fields($select=${select})` : 'fields')
    .top(top);

  if (filter) req = req.filter(filter);
  if (orderby) req = req.orderby(orderby);

  const items = [];
  let page = await req.get();
  while (page) {
    if (page.value) items.push(...page.value);
    if (page['@odata.nextLink']) {
      // eslint-disable-next-line no-await-in-loop
      page = await graph
        .api(page['@odata.nextLink'])
        .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
        .get();
    } else {
      page = null;
    }
  }
  // Flatten: surface `fields` at top level along with the SharePoint id and
  // the createdBy / lastModifiedBy person info (used by the Dashboard's
  // Recent Activity feed for "Added by"). These envelope fields are stripped
  // again on write by stripSystemFields, so they're read-only extras.
  return items.map((item) => ({
    id: item.id,
    createdBy: item.createdBy,
    lastModifiedBy: item.lastModifiedBy,
    ...item.fields,
  }));
}

export async function getItem(listKey, itemId) {
  const item = await graph
    .api(`${listBase(listKey)}/items/${itemId}`)
    .expand('fields')
    .get();
  return { id: item.id, ...item.fields };
}

// SharePoint's `fields` endpoint rejects any key that isn't a real, writable
// column. Our decoded rows spread every field we get back — including system
// columns like `Modified`, `Author`, and the item `id` — at the top level so
// callers can use them for routing and display. If any of those leak back
// into a write payload the whole PATCH rejects with "Field 'X' is not
// recognized" or "Field 'X' is read-only" and the save silently fails.
//
// Strip the well-known non-writable keys before every create/update. The set
// below covers both the Graph "item" envelope fields (`id`, `fields`, etc.)
// and the SharePoint system columns that come back inside `fields`.
const SYSTEM_FIELDS = new Set([
  // Graph item envelope
  'id',
  'fields',
  'contentType',
  'createdDateTime',
  'lastModifiedDateTime',
  'eTag',
  'webUrl',
  'parentReference',
  'createdBy',
  'lastModifiedBy',
  // SharePoint read-only system columns
  'Modified',
  'Created',
  'Author',
  'Editor',
  'AuthorLookupId',
  'EditorLookupId',
  'AppAuthor',
  'AppEditor',
  'AppAuthorLookupId',
  'AppEditorLookupId',
  'Attachments',
  'ContentType',
  'FileSystemObjectType',
  'ItemChildCount',
  'FolderChildCount',
  'LinkTitle',
  'LinkTitleNoMenu',
  'LinkTitle2',
  'Restricted',
  'ServerRedirectedEmbedUri',
  'ServerRedirectedEmbedUrl',
  'GUID',
  'WorkflowVersion',
  'WorkflowInstanceID',
  'ParentUniqueId',
]);
function stripSystemFields(input) {
  if (!input || typeof input !== 'object') return input;
  const out = {};
  for (const [k, v] of Object.entries(input)) {
    if (SYSTEM_FIELDS.has(k)) continue;
    if (k.startsWith('@') || k.startsWith('_')) continue; // @odata.*, _UIVersion, etc.
    if (k.startsWith('OData__')) continue; // escaped SharePoint internal columns
    out[k] = v;
  }
  return out;
}

// Pull the offending column name out of a Graph/SharePoint error message so
// we can drop it and retry. Two phrasings we've seen in practice:
//   "Field 'PlantClosuresJson' is not recognized"
//   "Field 'Modified' is read-only"
// Return the column name or null if this isn't a per-field rejection.
function extractFieldError(err) {
  const msg = err?.body?.error?.message || err?.message || '';
  const match = /Field '([^']+)' (?:is not recognized|is read-only)/.exec(msg);
  return match ? match[1] : null;
}

// Run a mutation and, when SharePoint rejects a single field as missing or
// read-only, drop that field from the payload and try again. This lets the
// app save successfully against lists whose schema has drifted from ours
// (e.g. a new JSON column that hasn't been provisioned yet) without forcing
// the user to run the provisioning script first.
async function runWithFieldDropRetry(listKey, payload, send) {
  let attempt = 0;
  let current = { ...payload };
  while (attempt < 20) {
    try {
      return await send(current);
    } catch (err) {
      const bad = extractFieldError(err);
      if (!bad || !(bad in current)) throw err;
      // eslint-disable-next-line no-console
      console.warn(
        `[sharepoint] ${listKey}: dropping unrecognized/read-only field '${bad}' and retrying.`,
      );
      const { [bad]: _drop, ...rest } = current;
      current = rest;
      attempt += 1;
    }
  }
  throw new Error(
    `[sharepoint] ${listKey}: gave up after dropping too many unrecognized fields.`,
  );
}

export async function createItem(listKey, fields) {
  const safe = stripSystemFields(fields);
  const created = await runWithFieldDropRetry(listKey, safe, (payload) =>
    graph.api(`${listBase(listKey)}/items`).post({ fields: payload }),
  );
  return { id: created.id, ...created.fields };
}

export async function updateItem(listKey, itemId, fields) {
  // PATCH on /fields updates column values without bumping system fields we
  // don't own. Returns the updated fields object only, so re-merge the id.
  const safe = stripSystemFields(fields);
  const updated = await runWithFieldDropRetry(listKey, safe, (payload) =>
    graph.api(`${listBase(listKey)}/items/${itemId}/fields`).patch(payload),
  );
  return { id: itemId, ...updated };
}

export async function deleteItem(listKey, itemId) {
  await graph.api(`${listBase(listKey)}/items/${itemId}`).delete();
  return { id: itemId, deleted: true };
}

/* ============================================================
 * Document-library helpers
 *
 * A document library is still a SharePoint list — the list-item endpoints
 * above keep working — but it also has an associated "drive" that holds
 * the actual files. These helpers wrap the file-oriented endpoints (list,
 * upload, delete) so the sales resources service doesn't have to know
 * about Graph drive IDs.
 *
 * For uploads we always use the upload-session flow, which works for
 * files up to ~250GB. Simple PUT is fine for <4MB but using sessions
 * universally keeps the code branch-free.
 * ========================================================== */

// Graph only needs the drive-id lookup once per library, so cache it.
// Keyed by listKey — the key we use in LIST_NAMES, not the display name.
const libraryDriveCache = new Map();

/**
 * Return `{ id: driveId, webUrl }` for the document library backing
 * `listKey`. Throws a friendly error if the list either doesn't exist or
 * exists but is a generic list (no /drive) — this is the case we hit when
 * an older `SalesResources` generic list is still in place from before the
 * library migration.
 */
export async function getLibraryDrive(listKey) {
  if (libraryDriveCache.has(listKey)) return libraryDriveCache.get(listKey);
  assertSite();
  const name = LIST_NAMES[listKey] || listKey;
  try {
    const drive = await graph
      .api(`/sites/${SITE_ID}/lists/${name}/drive`)
      .get();
    const out = { id: drive.id, webUrl: drive.webUrl };
    libraryDriveCache.set(listKey, out);
    return out;
  } catch (err) {
    const status = err?.statusCode || err?.status;
    // 404 (list missing) OR 400 "BadRequest" (list exists but isn't a
    // document library) both land here. Either way, the fix is the same:
    // provision the library. Surface that in plain language instead of the
    // Graph error.
    if (status === 404 || status === 400) {
      throw new Error(
        `The "${name}" document library isn't set up yet on this SharePoint ` +
          `site. Ask IT (or whoever runs the setup script) to run ` +
          `"npm run provision-lists" — if "${name}" exists as a plain list, ` +
          `delete it first so the script can recreate it as a document library.`,
      );
    }
    throw err;
  }
}

/**
 * List a document library's items with their drive metadata.
 *
 * Each returned row contains:
 *   - id              — listItem id (used for PATCH / DELETE)
 *   - driveItemId     — drive id (used for file delete if needed)
 *   - fileName        — display name of the uploaded file
 *   - sizeBytes       — file size
 *   - downloadUrl     — pre-authenticated, short-lived URL the browser
 *                       can hit directly (no auth header needed)
 *   - webUrl          — SharePoint preview URL
 *   - ...fields       — every custom column the Director set on upload
 *
 * Folders inside the library are ignored — we only surface files.
 */
export async function listLibraryItems(listKey, opts = {}) {
  const { top = 200, orderby } = opts;
  assertSite();
  const name = LIST_NAMES[listKey] || listKey;
  let req = graph
    .api(`/sites/${SITE_ID}/lists/${name}/items`)
    .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
    .expand('fields,driveItem')
    .top(top);
  if (orderby) req = req.orderby(orderby);

  const items = [];
  let page = await req.get();
  while (page) {
    if (page.value) items.push(...page.value);
    if (page['@odata.nextLink']) {
      // eslint-disable-next-line no-await-in-loop
      page = await graph
        .api(page['@odata.nextLink'])
        .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
        .get();
    } else {
      page = null;
    }
  }

  return items
    .filter((i) => i.driveItem && i.driveItem.file) // drop folder rows
    .map((i) => ({
      id: i.id,
      driveItemId: i.driveItem.id,
      fileName: i.driveItem.name,
      sizeBytes: i.driveItem.size,
      // The @microsoft.graph.downloadUrl claim is short-lived (minutes).
      // Callers should fetch fresh when they render; they shouldn't
      // cache it across route changes.
      downloadUrl: i.driveItem['@microsoft.graph.downloadUrl'] || null,
      webUrl: i.driveItem.webUrl,
      ...i.fields,
    }));
}

// Simple direct upload for small files — goes to graph.microsoft.com
// like every other portal call, so it can never hit the CORS/proxy
// blocks that the tenant-domain upload-session URL sometimes does
// (seen in production July 2026 as "PO file upload failed: Failed to
// fetch"). Graph's simple-PUT limit is 4 MB; bigger files fall back
// to the upload-session path below.
const SIMPLE_UPLOAD_LIMIT_BYTES = 4 * 1024 * 1024;
async function uploadSmallDirect(driveId, filename, file) {
  const token = await getAccessToken();
  const safeName = encodeURIComponent(filename).replace(/%2F/g, '/');
  const url =
    `https://graph.microsoft.com/v1.0/drives/${driveId}` +
    `/root:/${safeName}:/content?@microsoft.graph.conflictBehavior=rename`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`upload failed (${res.status}): ${text}`);
  }
  return res.json();
}

// File-uploads to SharePoint's upload session are plain fetch calls,
// not authenticated Graph SDK calls — the uploadUrl itself is the
// capability. We still need a token for the *session creation* step
// since that goes through Graph.
async function createUploadSession(driveId, filename) {
  const token = await getAccessToken();
  // Use root:/.../{filename}:/createUploadSession so SharePoint places
  // the file at the library root. `conflictBehavior: rename` ensures
  // we don't fail when a duplicate name exists — SharePoint appends a
  // counter like "deck (1).pdf".
  const safeName = encodeURIComponent(filename).replace(/%2F/g, '/');
  const url =
    `https://graph.microsoft.com/v1.0/drives/${driveId}` +
    `/root:/${safeName}:/createUploadSession`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      item: {
        '@microsoft.graph.conflictBehavior': 'rename',
        name: filename,
      },
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`createUploadSession failed (${res.status}): ${text}`);
  }
  return res.json();
}

// Upload the bytes in a single PUT. SharePoint's upload-session URL
// accepts a single-range upload for files up to its 60 MiB per-request
// limit — well above the size of any realistic one-pager or deck.
// If a file happens to exceed that we fall back to chunked uploads.
const UPLOAD_CHUNK_BYTES = 50 * 1024 * 1024; // 50 MiB, well under the 60 MiB cap
async function uploadToSession(uploadUrl, file) {
  const total = file.size;
  if (total <= UPLOAD_CHUNK_BYTES) {
    // Note: Content-Length is a Fetch-spec "forbidden header name" — the
    // browser sets it from the body automatically and will silently drop
    // any value we try to supply. Only Content-Range is needed here.
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Range': `bytes 0-${total - 1}/${total}`,
      },
      body: file,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`upload failed (${res.status}): ${text}`);
    }
    return res.json();
  }

  // Chunked path — upload UPLOAD_CHUNK_BYTES at a time. SharePoint
  // returns 202 Accepted for intermediate chunks and 200/201 with
  // the final DriveItem on the last chunk.
  let start = 0;
  let lastBody = null;
  while (start < total) {
    const end = Math.min(start + UPLOAD_CHUNK_BYTES, total) - 1;
    const chunk = file.slice(start, end + 1);
    // eslint-disable-next-line no-await-in-loop
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        // Content-Length is a forbidden header; browser sets it from body.
        'Content-Range': `bytes ${start}-${end}/${total}`,
      },
      body: chunk,
    });
    if (!res.ok && res.status !== 202) {
      // eslint-disable-next-line no-await-in-loop
      const text = await res.text();
      throw new Error(`upload chunk failed (${res.status}): ${text}`);
    }
    // eslint-disable-next-line no-await-in-loop
    lastBody = res.status === 202 ? null : await res.json();
    start = end + 1;
  }
  return lastBody;
}

/**
 * Upload a File/Blob into a document library and set custom list-item
 * fields on the resulting row. Returns the listItem id so the caller can
 * refresh lists without refetching.
 *
 * @param {string} listKey      — library key in LIST_NAMES
 * @param {File|Blob} file      — the file to upload (must have a .name)
 * @param {object} [fields={}]  — custom column values (Title, Description, …)
 */
export async function uploadLibraryFile(listKey, file, fields = {}) {
  if (!file || !file.name) {
    throw new Error('uploadLibraryFile: a File with a .name is required.');
  }
  const drive = await getLibraryDrive(listKey);
  // Small files (≤4 MB — every realistic PO / receipt / one-pager) go
  // straight to graph.microsoft.com, dodging the tenant-domain
  // upload-session URL that some browser/network combinations block
  // ("Failed to fetch"). Bigger files still use the chunked session.
  let driveItem;
  if (file.size <= SIMPLE_UPLOAD_LIMIT_BYTES) {
    driveItem = await uploadSmallDirect(drive.id, file.name, file);
  } else {
    const session = await createUploadSession(drive.id, file.name);
    driveItem = await uploadToSession(session.uploadUrl, file);
  }

  // Follow the drive item to its listItem so we can PATCH our custom
  // columns (SharePoint won't let us set them on the upload call itself).
  const listItem = await graph
    .api(`/drives/${drive.id}/items/${driveItem.id}/listItem`)
    .expand('fields')
    .get();

  const safeFields = stripSystemFields(fields);
  // Only PATCH if we actually have fields to set — avoids an empty PATCH
  // call for resources with no metadata.
  if (Object.keys(safeFields).length) {
    await runWithFieldDropRetry(listKey, safeFields, (payload) =>
      graph
        .api(`${listBase(listKey)}/items/${listItem.id}/fields`)
        .patch(payload),
    );
  }

  return {
    id: listItem.id,
    driveItemId: driveItem.id,
    fileName: driveItem.name,
    sizeBytes: driveItem.size,
    // SharePoint preview URL for the uploaded file. Callers that want
    // a clickable "open in new window" link (e.g. timesheet receipts)
    // store this on their list row so the file stays one click away
    // without re-querying the library.
    webUrl: driveItem.webUrl || '',
  };
}

/**
 * Delete a document-library row by listItem id. Removes the underlying
 * file too (deleting the listItem detaches and trashes the drive item).
 */
export async function deleteLibraryItem(listKey, itemId) {
  await graph.api(`${listBase(listKey)}/items/${itemId}`).delete();
  return { id: itemId, deleted: true };
}
